/**
 * Torrentio API service
 * Fetches torrent streams for movies using IMDB ID
 */

const TORRENTIO_BASE = "https://torrentio.strem.fun";

/**
 * Get Electron's local proxy URL if available
 */
const getElectronProxyUrl = () => {
  if (typeof window !== "undefined" && window.electronAPI?.proxyUrl) {
    return window.electronAPI.proxyUrl;
  }
  return null;
};

/**
 * Get the URL with proxy if needed (Electron local proxy > Cloudflare)
 */
const getProxiedUrl = (url) => {
  // In Electron, use local proxy (bypasses ISP blocks)
  const electronProxy = getElectronProxyUrl();
  if (electronProxy) {
    return `${electronProxy}?url=${encodeURIComponent(url)}`;
  }
  // Fallback: direct request (works if not blocked)
  return url;
};

/**
 * Get available torrent streams for a movie
 * @param {string} imdbId - The IMDB ID (e.g., "tt1234567")
 * @returns {Promise<Array>} List of available streams
 */
export const getTorrentStreams = async (imdbId) => {
  if (!imdbId) {
    return [];
  }

  try {
    // Torrentio API endpoint - use proxy to bypass ISP blocking
    const directUrl = `${TORRENTIO_BASE}/stream/movie/${imdbId}.json`;
    const response = await fetch(getProxiedUrl(directUrl));

    if (!response.ok) {
      console.error("Torrentio API error:", response.status);
      return [];
    }

    const data = await response.json();

    if (!data.streams || data.streams.length === 0) {
      return [];
    }

    // Parse and format the streams
    return data.streams
      .map((stream) => {
        // Parse the title to extract quality info
        const title = stream.title || "";
        const name = stream.name || "";

        // Extract quality (720p, 1080p, 4K, etc.)
        const qualityMatch = title.match(/(\d{3,4}p|4K|2160p)/i);
        const quality = qualityMatch ? qualityMatch[1].toUpperCase() : "Unknown";

        // Extract size
        const sizeMatch = title.match(/💾\s*([\d.]+\s*(?:GB|MB))/i);
        const size = sizeMatch ? sizeMatch[1] : "";

        // Extract seeders
        const seedersMatch = title.match(/👤\s*(\d+)/);
        const seeders = seedersMatch ? parseInt(seedersMatch[1]) : 0;

        // Check audio compatibility (browser-incompatible codecs)
        const incompatibleAudio = /TrueHD|Atmos|DTS[-.]?HD|DTS[-.]?X|FLAC|7\.1|5\.1.*(?:TrueHD|DTS)/i.test(title + name);
        const compatibleAudio = /AAC|AC3|MP3|Opus|EAC3/i.test(title + name);
        const audioCompatible = compatibleAudio && !incompatibleAudio;

        // Check video compatibility (HDR/Dolby Vision may not work in browsers)
        const hasHDR = /HDR|HDR10|HDR10\+|HLG/i.test(title + name);
        const hasDV = /DV|Dolby[\s.]?Vision|DoVi/i.test(title + name);
        const hasHEVC = /HEVC|H\.?265|x265/i.test(title + name);
        const videoCompatible = !hasHDR && !hasDV;

        // Overall browser compatibility
        const browserCompatible = audioCompatible && videoCompatible;

        return {
          name: name,
          title: title,
          quality,
          size,
          seeders,
          audioCompatible,
          videoCompatible,
          hasHDR,
          hasDV,
          hasHEVC,
          browserCompatible,
          infoHash: stream.infoHash,
          fileIdx: stream.fileIdx,
          // Build magnet URL
          magnetUrl: stream.infoHash
            ? `magnet:?xt=urn:btih:${stream.infoHash}&dn=${encodeURIComponent(name)}&tr=udp://tracker.opentrackr.org:1337&tr=udp://open.stealth.si:80/announce&tr=udp://tracker.torrent.eu.org:451&tr=udp://tracker.bittor.pw:1337&tr=udp://public.popcorn-tracker.org:6969&tr=udp://tracker.dler.org:6969&tr=udp://exodus.desync.com:6969`
            : null,
        };
      })
      .filter((stream) => stream.magnetUrl); // Only return streams with valid magnet URLs
  } catch (error) {
    console.error("Error fetching torrent streams:", error);
    return [];
  }
};

/**
 * Sort streams by quality and seeders
 */
export const sortStreams = (streams) => {
  const qualityOrder = { "4K": 4, "2160P": 4, "1080P": 3, "720P": 2, UNKNOWN: 1 };

  return [...streams].sort((a, b) => {
    // First by quality
    const qualityDiff = (qualityOrder[b.quality] || 0) - (qualityOrder[a.quality] || 0);
    if (qualityDiff !== 0) return qualityDiff;

    // Then by seeders
    return b.seeders - a.seeders;
  });
};
